import { apiUrl } from "./api";
import { StatsResponse, StatsSummaryResponse, StatsTags } from "../types/stats";

export interface FetchStatsParams {
  pilot_uid?: string;
  schema_name?: string | string[];
  tags?: StatsTags;
  limit?: number;
  order?: "count" | "value_total" | "last_occurred_at";
  direction?: "asc" | "desc";
}

export async function fetchStats(params: FetchStatsParams = {}): Promise<StatsResponse> {
  const url = apiUrl("/stats");
  const query = new URLSearchParams();

  if (params.pilot_uid) {
    query.set("pilot_uid", params.pilot_uid);
  }

  if (params.schema_name) {
    const schemas = Array.isArray(params.schema_name) ? params.schema_name : [params.schema_name];
    if (schemas.length > 0) {
      query.set("schema_name", schemas.join(","));
    }
  }

  if (params.tags && Object.keys(params.tags).length > 0) {
    query.set("tags", JSON.stringify(params.tags));
  }

  if (params.limit != null) {
    query.set("limit", String(params.limit));
  }

  if (params.order) {
    query.set("order", params.order);
  }

  if (params.direction) {
    query.set("direction", params.direction);
  }

  url.search = query.toString();

  const response = await fetch(url.toString());

  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new Error(message);
  }

  return (await response.json()) as StatsResponse;
}

export interface FetchStatsSummaryParams {
  schema_name?: string | string[];
  tags?: StatsTags;
  limit?: number;
  order?: "event_count" | "value_total" | "pilot_count";
  direction?: "asc" | "desc";
}

// Community totals across all pilots, aggregated server-side.
export async function fetchStatsSummary(params: FetchStatsSummaryParams = {}): Promise<StatsSummaryResponse> {
  const url = apiUrl("/stats/summary");
  const query = new URLSearchParams();

  if (params.schema_name) {
    const schemas = Array.isArray(params.schema_name) ? params.schema_name : [params.schema_name];
    if (schemas.length > 0) query.set("schema_name", schemas.join(","));
  }
  if (params.tags && Object.keys(params.tags).length > 0) query.set("tags", JSON.stringify(params.tags));
  if (params.limit != null) query.set("limit", String(params.limit));
  if (params.order) query.set("order", params.order);
  if (params.direction) query.set("direction", params.direction);

  url.search = query.toString();
  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(await extractErrorMessage(response));
  return (await response.json()) as StatsSummaryResponse;
}

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (data && typeof data === "object" && "error" in data && typeof data.error === "string") {
      return data.error;
    }
  } catch (error) {
    // ignore
  }

  return `Request failed with status ${response.status}`;
}
